// eslint-disable-next-line import/no-extraneous-dependencies
import {Link} from 'gatsby'
import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

import {
  getKeyrusAiCopy,
  getKeyrusAiLeadCountry,
  getKeyrusAiPath,
  hasKeyrusAiSeenCookie,
  hasKeyrusAiSessionSeen,
  isKeyrusAiPathname,
  KEYRUS_AI_BANNER_HEIGHT_CSS_VAR,
  KEYRUS_AI_GATEWAY_FORCE_SHOW_ON_RELOAD,
  KEYRUS_AI_NAV_COMPACT_QUERY,
} from 'helpers/keyrusAi'
import KeyrusAiLandingContent from 'components/KeyrusAiLandingContent'

import bannerArtwork from './assets/banner-keyrus-ai.svg'
import useBannerStyles from './bannerStyles'

const EVENT_CONTINUE = 'keyrus_positioning_gateway_continue'
const EVENT_DISCOVER = 'keyrus_positioning_gateway_discover'
const EVENT_VIEW = 'keyrus_positioning_gateway_view'

const CONTINUE_LABEL_FALLBACK = 'Continue to the Keyrus website'
const MODAL_LABEL = 'Keyrus AI — Architect of intelligence'

const MONTSERRAT_FONT_URL =
  'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600;700;800&family=Poppins:wght@400&display=swap'
const FONT_LINK_ID = 'keyrus-ai-fonts'

const ensureMontserratLoaded = () => {
  if (
    typeof document === 'undefined' ||
    document.getElementById(FONT_LINK_ID)
  ) {
    return
  }

  const link = document.createElement('link')

  link.id = FONT_LINK_ID
  link.rel = 'stylesheet'
  link.href = MONTSERRAT_FONT_URL
  document.head.appendChild(link)
}

const gatewayRuntime = {
  dismissed: false,
}

const pushGatewayEvent = event => {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({event})
}

const getFocusableElements = container =>
  Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter(el => !el.hasAttribute('disabled') && el.tabIndex >= 0)

function getStyles(theme, isCompact) {
  const palette = theme.palette || {}
  const background = palette.background || {}
  const primary = palette.primary || {}
  const text = palette.text || {}
  const shadow = theme.shadow || {}
  const deepNavy = background.deepNavy || '#171531'
  const lightText = text.light || primary.contrastText || '#ffffff'
  const baseZIndex = theme.customZindex?.header || 1000000

  return {
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: baseZIndex + 2,
      overflowY: 'auto',
      background: deepNavy,
      color: lightText,
      fontFamily: "'Montserrat', sans-serif",
    },
    topContinueButton: {
      position: 'fixed',
      top: isCompact ? 19 : 18,
      left: isCompact ? 12 : undefined,
      right: isCompact ? undefined : 20,
      zIndex: baseZIndex + 3,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 36,
      maxWidth: 'calc(100vw - 24px)',
      padding: '8px 12px',
      borderRadius: 6,
      background: 'rgba(7, 16, 31, 0.62)',
      color: lightText,
      border: '1px solid rgba(255, 255, 255, 0.28)',
      boxShadow: shadow.keys || '0 8px 22px rgba(0, 0, 0, 0.32)',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.2,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    },
    banner: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: baseZIndex + 1,
      color: '#1b1d26',
      boxShadow: '0 3px 10px rgba(27, 29, 38, 0.12)',
    },
  }
}

const KeyrusAiGateway = ({countryData, enabled, modalDisabled, nodeLocale}) => {
  const theme = useTheme()
  const bannerClasses = useBannerStyles()
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const shouldSkipPath = isKeyrusAiPathname(pathname)
  const [isVisible, setIsVisible] = useState(false)
  const [isDismissed, setIsDismissed] = useState(gatewayRuntime.dismissed)
  const dialogRef = useRef(null)
  const bannerRef = useRef(null)
  const copy = getKeyrusAiCopy(nodeLocale)
  const continueLabel = copy.continueLabel || CONTINUE_LABEL_FALLBACK
  const keyrusAiHref = getKeyrusAiPath(countryData)
  const leadCountry = getKeyrusAiLeadCountry(countryData)
  const isCompact = useMediaQuery(KEYRUS_AI_NAV_COMPACT_QUERY)
  const styles = getStyles(theme, isCompact)
  const isActiveContext = enabled && !shouldSkipPath

  const dismiss = useCallback(eventName => {
    gatewayRuntime.dismissed = true
    setIsDismissed(true)
    pushGatewayEvent(eventName)
    setIsVisible(false)
  }, [])

  useEffect(() => {
    if (isVisible) {
      ensureMontserratLoaded()
    }
  }, [isVisible])

  useEffect(() => {
    if (!enabled || isKeyrusAiPathname(window.location.pathname)) {
      setIsVisible(false)

      return
    }

    // Never open the intro modal when disabled, but keep the slim banner. Behave as if
    // the gateway had already been dismissed so the banner branch renders.
    if (modalDisabled) {
      setIsDismissed(true)
      setIsVisible(false)

      return
    }

    if (gatewayRuntime.dismissed || hasKeyrusAiSessionSeen()) {
      gatewayRuntime.dismissed = true
      setIsDismissed(true)
      setIsVisible(false)

      return
    }

    if (!KEYRUS_AI_GATEWAY_FORCE_SHOW_ON_RELOAD && hasKeyrusAiSeenCookie()) {
      gatewayRuntime.dismissed = true
      setIsDismissed(true)
      setIsVisible(false)

      return
    }

    setIsDismissed(false)
    setIsVisible(true)
  }, [enabled, modalDisabled, pathname])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    const shouldReserveBannerSpace =
      isActiveContext && !isVisible && isDismissed

    if (!shouldReserveBannerSpace) {
      document.documentElement.style.removeProperty(
        KEYRUS_AI_BANNER_HEIGHT_CSS_VAR,
      )

      return undefined
    }

    const updateBannerSpace = () => {
      const height = bannerRef.current?.getBoundingClientRect().height || 0

      document.documentElement.style.setProperty(
        KEYRUS_AI_BANNER_HEIGHT_CSS_VAR,
        `${height}px`,
      )
    }
    const observer = new ResizeObserver(updateBannerSpace)

    updateBannerSpace()
    if (bannerRef.current) {
      observer.observe(bannerRef.current)
    }

    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty(
        KEYRUS_AI_BANNER_HEIGHT_CSS_VAR,
      )
    }
  }, [isActiveContext, isDismissed, isVisible])

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    pushGatewayEvent(EVENT_VIEW)
    // "Seen this session" is recorded by KeyrusAiLandingContent on mount — it
    // renders here in the modal AND on the standalone /keyrus-ai page, so seeing
    // either one counts once. See markKeyrusAiSeen in helpers/keyrusAi.

    const previousOverflow = document.body.style.overflow

    document.body.style.overflow = 'hidden'

    const focusable = dialogRef.current
      ? getFocusableElements(dialogRef.current)
      : []
    const [first] = focusable

    if (first) {
      first.focus()
    }

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismiss(EVENT_CONTINUE)

        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return
      }

      const focusable = getFocusableElements(dialogRef.current)

      if (!focusable.length) {
        return
      }

      const [first] = focusable
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [dismiss, isVisible])

  if (!isActiveContext) {
    return null
  }

  if (!isVisible && isDismissed) {
    return (
      <div
        aria-label={copy.bannerText}
        ref={bannerRef}
        className={bannerClasses.surface}
        role="region"
        style={styles.banner}
      >
        <Link
          onClick={() => pushGatewayEvent(EVENT_DISCOVER)}
          className={bannerClasses.link}
          to={keyrusAiHref}
        >
          <span className={bannerClasses.artwork} aria-hidden="true">
            <img src={bannerArtwork} alt="" width="1440" height="67" />
          </span>
          <span className={bannerClasses.copy}>
            <span className={bannerClasses.title}>{copy.bannerText}</span>
            <span className={bannerClasses.description}>
              {copy.bannerDescription}
            </span>
          </span>
          <span className={bannerClasses.cta}>{copy.bannerCtaLabel}</span>
        </Link>
      </div>
    )
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      aria-label={MODAL_LABEL}
      aria-modal="true"
      ref={dialogRef}
      role="dialog"
      style={styles.overlay}
    >
      <button
        aria-label={continueLabel}
        onClick={() => dismiss(EVENT_CONTINUE)}
        style={styles.topContinueButton}
        type="button"
      >
        {continueLabel}
      </button>
      <KeyrusAiLandingContent
        nodeLocale={nodeLocale}
        salesforceCountry={leadCountry}
      />
    </div>
  )
}

KeyrusAiGateway.propTypes = {
  countryData: PropTypes.shape({
    countryCode: PropTypes.string,
    localeCode: PropTypes.string,
    technicalName: PropTypes.string,
  }),
  enabled: PropTypes.bool,
  modalDisabled: PropTypes.bool,
  nodeLocale: PropTypes.string,
}

KeyrusAiGateway.defaultProps = {
  countryData: null,
  enabled: false,
  modalDisabled: false,
  nodeLocale: 'en-US',
}

export default KeyrusAiGateway
